import * as React from "react"
import { useFluent, useThemeClassName } from "@fluentui/react-components"

/**
 * Per-dialog stacking layer.
 *
 * Every open dialog gets its own mount node appended to the end of <body> at open time, styled like a
 * Fluent portal node (same theme class, `z-index: 1_000_000`). Stack order therefore equals open order —
 * a confirm opened from a dialog, a dialog opened from an OverlayDrawer, or a drawer opened from a dialog
 * always lands on top — without ever moving a node that already holds an open dialog. Re-inserting such a
 * node restarts its CSS enter animation, which is what made parent dialogs "close and reopen".
 */
export const DialogLayerContext = React.createContext<HTMLElement | undefined>(undefined)

/** Mount node of the nearest open dialog, or `undefined` outside dialogs. */
export function useDialogLayer() {
  return React.useContext(DialogLayerContext)
}

const EXIT_GRACE_MS = 300

export function useDialogLayerNode(open: boolean) {
  const { targetDocument, dir } = useFluent()
  const themeClassName = useThemeClassName()
  const [node, setNode] = React.useState<HTMLElement | undefined>(undefined)

  React.useLayoutEffect(() => {
    if (!open || !targetDocument) return
    const el = targetDocument.createElement("div")
    el.setAttribute("data-dialog-layer", "")
    el.style.cssText = "position:absolute;top:0;left:0;right:0;z-index:1000000"
    targetDocument.body.appendChild(el)
    setNode(el)
    return () => {
      // Keep the node until the exit animation has unmounted its content.
      const release = () => {
        if (el.childElementCount > 0) {
          window.setTimeout(release, 100)
          return
        }
        el.remove()
        setNode((cur) => (cur === el ? undefined : cur))
      }
      window.setTimeout(release, EXIT_GRACE_MS)
    }
  }, [open, targetDocument])

  React.useLayoutEffect(() => {
    if (!node) return
    node.className = themeClassName
    node.setAttribute("dir", dir)
  }, [node, themeClassName, dir])

  return node
}
